import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ControlSelectComponent, SelectOption } from '@form';
import { TenantsService } from '../../services/tenants.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { NzSizeLDSType } from 'ng-zorro-antd/core/types';

/**
 * TenantSelectorComponent - A component for selecting multiple tenants.
 * Features tenant sorting, display formatting, and state management.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ControlSelectComponent, ReactiveFormsModule, TranslocoDirective],
  encapsulation: ViewEncapsulation.None,
  selector: 'app-tenant-selector',
  template: `
    <ng-container *transloco="let t">
      <div
        class="flex items-center"
        [class.flex-row]="layout() === 'row'"
        [class.flex-col]="layout() === 'column'"
      >
        <control-select
          class="tenant-selector min-w-full"
          [control]="control"
          [placeholder]="t('TENANTS_SELECT')"
          [options]="sortedTenants()"
          [mode]="'multiple'"
          [size]="size()"
          [multipleTemplate]="multipleTemplate"
          [class.hide]="hideBackground()"
          [borderless]="borderless()"
          [isDisabled]="loadingApi()"
        />
        @if (displayWarning()) {
          <span class="text-red-4 text-xs min-w-full py-1">
            {{ t(warningMessage()) }}
          </span>
        }
      </div>
      <ng-template #multipleTemplate let-selected>
        <span class="mr-1">
          {{ formatTenantLabel(selected.nzLabel, this.maxTenantChars()) }}
        </span>
      </ng-template>
    </ng-container>
  `,
  styles: [
    `
      .tenant-selector.hide .ant-select-multiple .ant-select-selection-item {
        background-color: transparent;
        border: none;
      }
    `,
  ],
})
export class TenantSelectorComponent implements OnInit, OnDestroy {
  private readonly tenantsService = inject(TenantsService);
  loadingApi = signal<boolean>(false);
  protected readonly DEFAULT_WARNING_MESSAGE =
    'MESSAGES.WARNING.TENANTS_MIN_LENGTH';

  initSelectedTenants = input<boolean>(true);
  borderless = input<boolean>(false);
  maxTenantChars = input<number>(50);
  size = input<NzSizeLDSType>('small');
  layout = input<'row' | 'column'>('row');
  sendTenants = output<string[]>();

  displayWarning = signal<boolean>(false);
  warningMessage = signal<string>(this.DEFAULT_WARNING_MESSAGE);

  protected readonly FORM_CONTROL_NAME = 'name';
  protected readonly form = new FormGroup({
    [this.FORM_CONTROL_NAME]: new FormControl<string[]>([]),
  });
  protected readonly control = this.form.get(
    this.FORM_CONTROL_NAME,
  ) as FormControl<string[]>;
  private warningTimeoutId: ReturnType<typeof setTimeout> | null = null;

  selectedTenants = signal<string[]>([]);
  hideBackground = signal<boolean>(true);

  tenantOptions = toSignal(this.tenantsService.getTenantOptions(), {
    initialValue: [],
  });
  sortedTenants = computed(() =>
    this.sortTenants(this.selectedTenants(), this.tenantOptions()),
  );

  ngOnInit(): void {
    // subscribe to the loading$ observable and update the boolean signal on each emission
    this.initializeTenants();
    this.setupValueChanges();
  }

  ngOnDestroy(): void {
    // Clear timeout when the component is destroyed
    if (this.warningTimeoutId !== null) {
      clearTimeout(this.warningTimeoutId);
    }
  }

  /**
   * Initializes tenants and sorts them.
   */
  private initializeTenants(): void {
    const selectedTenants = this.tenantsService.retrieveSelectedTenants();
    this.selectedTenants.set(selectedTenants);
    if (this.initSelectedTenants()) {
      this.control.setValue(selectedTenants);
    }
  }

  /**
   * Sets up subscription to form control value changes.
   * Ensures that at least one tenant is always selected.
   */
  private setupValueChanges(): void {
    this.control.valueChanges.subscribe((selected: string[] | null) => {
      const selectedTenants = selected?.length
        ? selected
        : this.selectedTenants(); // Retain previous tenants if the list is empty.

      if (!selected?.length) {
        console.warn(
          'At least one tenant must be selected. Reverting to the last valid selection.',
        );
        this.control.setValue(this.selectedTenants(), { emitEvent: false }); // Prevent triggering a new value change event.
        this.showWarning();
      } else {
        this.displayWarning.set(false);
      }

      this.selectedTenants.set(selectedTenants);
      this.tenantsService.saveSelectedTenants(selectedTenants);
      this.sendTenants.emit(selectedTenants);
    });
  }

  /**
   * Updates the sorted list of tenants based on selected values.
   * @param selectedValues - Array of selected tenant values.
   * @param tenantOptions
   */
  private sortTenants(
    selectedValues: string[],
    tenantOptions: SelectOption[],
  ): SelectOption[] {
    const selectedSet = new Set(selectedValues);

    return [
      ...tenantOptions.filter((tenant) => selectedSet.has(tenant.value)),
      ...tenantOptions.filter((tenant) => !selectedSet.has(tenant.value)),
    ];
  }

  /**
   * Formats a tenant label to truncate if it's too long.
   * @param label - The label to format.
   * @param maxChars
   * @returns The formatted label.
   */
  protected formatTenantLabel(label: string, maxChars: number): string {
    const maxLength = maxChars;
    return label.length > maxLength
      ? `${label.substring(0, maxLength).trim()}...`
      : label;
  }

  private showWarning(): void {
    // Clear any existing timeout to prevent overlapping
    if (this.warningTimeoutId !== null) {
      clearTimeout(this.warningTimeoutId);
    }

    this.displayWarning.set(true);
    this.warningTimeoutId = setTimeout(() => {
      this.displayWarning.set(false);
      this.warningTimeoutId = null; // Reset timeout ID
    }, 5000); // Remove the warning after 3 seconds
  }
}
